/**
 * Centralized date-driven occupancy status utility.
 *
 * Business Rules:
 *   Occupied    → check-in completed AND today >= arrivalDate AND today < departureDate
 *   Reserved    → reservation exists AND arrivalDate > today (future arrival)
 *   Maintenance → room marked unavailable / maintenance flag
 *   Available   → no reservation, no occupancy, no maintenance
 */

/**
 * Returns today's date as a YYYY-MM-DD string (local midnight).
 */
export function getTodayStr() {
  const d = new Date();
  return `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}-${String(d?.getDate())?.padStart(2, '0')}`;
}

/**
 * Determine the occupancy status for a single stay record using date-driven logic.
 *
 * @param {object} stay  - Normalized stay record with fields:
 *   status        {string}  - DB status ('confirmed-stay' | 'active-reservation' | 'completed-stay' | ...)
 *   checkIn       {string}  - Arrival date YYYY-MM-DD
 *   checkOut      {string}  - Departure date YYYY-MM-DD
 * @param {string} today - Today's date as YYYY-MM-DD (pass getTodayStr() result)
 * @returns {'Occupied'|'Reserved'|'Available'}
 */
export function getStayOccupancyStatus(stay, today) {
  if (!stay) return 'Available';

  const checkIn  = stay?.checkIn  || stay?.check_in_date  || '';
  const checkOut = stay?.checkOut || stay?.check_out_date || '';
  const status   = stay?.status   || '';

  // OCCUPIED: check-in completed AND today is within the stay window
  const isCheckedIn = status === 'confirmed-stay' || status === 'active';
  if (isCheckedIn && checkIn && checkOut) {
    if (today >= checkIn && today < checkOut) {
      return 'Occupied';
    }
  }

  // RESERVED: any reservation (active-reservation / pending) with future arrival
  const isReservation = status === 'active-reservation' || status === 'pending';
  if (isReservation && checkIn) {
    if (checkIn > today) {
      return 'Reserved';
    }
    // Reservation arrival date is today or past but not yet checked in → still Reserved
    // (they haven't checked in yet, so not Occupied)
    return 'Reserved';
  }

  return 'Available';
}

/**
 * Determine the display status for a room given all its active stays and the room record.
 *
 * Priority: Occupied > Reserved > Maintenance > Available
 *
 * @param {string}   roomNumber  - Full room number e.g. "H-101"
 * @param {object[]} stays       - All stay records (normalized with checkIn/checkOut/status)
 * @param {object}   roomRecord  - Room record from DB (may have cleaning_status, is_active)
 * @param {string}   today       - Today YYYY-MM-DD
 * @returns {'Occupied'|'Reserved'|'Maintenance'|'Available'}
 */
export function getRoomOccupancyStatus(roomNumber, stays, roomRecord, today) {
  // Maintenance check first (room-level flag)
  if (roomRecord) {
    const cs = roomRecord?.cleaning_status || '';
    const isActive = roomRecord?.is_active !== false;
    if (!isActive || cs === 'maintenance' || cs === 'out-of-order') {
      return 'Maintenance';
    }
  }

  // Find all non-completed stays for this room
  const roomStays = stays?.filter(s => {
    const rn = (s?.room_number || s?.room || '')?.trim();
    return rn === roomNumber && s?.status !== 'completed-stay' && s?.status !== 'checked-out';
  }) || [];

  if (roomStays?.length === 0) return 'Available';

  // Check for Occupied first (highest priority)
  for (const stay of roomStays) {
    if (getStayOccupancyStatus(stay, today) === 'Occupied') {
      return 'Occupied';
    }
  }

  // Check for Reserved
  for (const stay of roomStays) {
    if (getStayOccupancyStatus(stay, today) === 'Reserved') {
      return 'Reserved';
    }
  }

  return 'Available';
}

/**
 * Generate a validation report for specific rooms.
 * Returns an array of report rows for display.
 *
 * @param {string[]} roomNumbers - e.g. ['H-101', 'H-001']
 * @param {object[]} stays       - All normalized stays
 * @param {object[]} rooms       - All room records
 * @returns {object[]}
 */
export function generateValidationReport(roomNumbers, stays, rooms) {
  const today = getTodayStr();
  return roomNumbers?.map(roomNumber => {
    const roomRecord = rooms?.find(r => (r?.room_number || r?.room) === roomNumber);
    const roomStays  = stays?.filter(s => (s?.room_number || s?.room || '')?.trim() === roomNumber);
    const activeStay = roomStays?.find(s => s?.status !== 'completed-stay' && s?.status !== 'checked-out');

    const correctedStatus = getRoomOccupancyStatus(roomNumber, stays, roomRecord, today);

    // Determine "current" status from raw DB status field
    let currentStatus = 'Available';
    if (activeStay) {
      if (activeStay?.status === 'confirmed-stay' || activeStay?.status === 'active') currentStatus = 'Occupied (by status only)';
      else if (activeStay?.status === 'active-reservation' || activeStay?.status === 'pending') currentStatus = 'Reserved (by status only)';
    }

    const checkIn  = activeStay?.checkIn  || activeStay?.check_in_date  || '—';
    const checkOut = activeStay?.checkOut || activeStay?.check_out_date || '—';
    const stayStatus = activeStay?.status || '—';

    let rootCause = 'No active stay found.';
    let fixApplied = 'Shown as Available.';

    if (activeStay) {
      const isCheckedIn = stayStatus === 'confirmed-stay' || stayStatus === 'active';
      const isReservation = stayStatus === 'active-reservation' || stayStatus === 'pending';

      if (isCheckedIn && checkIn !== '—' && checkOut !== '—') {
        if (today >= checkIn && today < checkOut) {
          rootCause  = 'Check-in completed and today is within stay window.';
          fixApplied = 'Correctly shown as Occupied (Red).';
        } else if (today >= checkOut) {
          rootCause  = 'Check-in completed but departure date has passed — stay should be completed.';
          fixApplied = 'Shown as Available (stay expired).';
        } else {
          rootCause  = 'Check-in completed but arrival date is in the future (unusual).';
          fixApplied = 'Shown as Reserved until arrival date.';
        }
      } else if (isReservation) {
        if (checkIn > today) {
          rootCause  = 'Reservation exists with future arrival date — resident has not yet arrived.';
          fixApplied = 'Correctly shown as Reserved (Yellow).';
        } else {
          rootCause  = 'Reservation exists but arrival date is today or past — awaiting check-in.';
          fixApplied = 'Shown as Reserved (Yellow) — perform check-in to mark Occupied.';
        }
      }
    }

    return {
      roomNumber,
      arrivalDate:      checkIn,
      departureDate:    checkOut,
      checkInStatus:    stayStatus,
      currentStatus,
      correctedStatus,
      rootCause,
      fixApplied,
    };
  });
}
